/*
 * Copyright © 2020 by Pactera Technology Japan Co., Ltd.
 *
 * Code written by Atilika Inc. Contact us at hello@atilika.com.
 */

import parseISO from "date-fns/parseISO";
import { JsonAny } from "../../Types";
import { parseVideoReaction, VideoReaction } from "./VideoReaction";
import { parseVideoComment, VideoComment } from "./VideoComment";
import { CaptionLanguage, parseVideoCaption, VideoCaption } from "./VideoCaption";
import { parseVideoExtraMaterial } from "./VideoExtraMaterial";
import { Group } from "../../../user/User";

export type ReactionType = "LIKE" | "CHEERS" | "SURPRISED";
export const REACTION_TYPES: ReactionType[] = ["LIKE", "CHEERS", "SURPRISED"];

export type VideoCaptionLanguage = "ja" | "en" | "zh-Hans" | "vi";

export interface Video {
    id: number;
    imageUrl: string;
    sourceUrl: string;
    playUrl: string;
    downloadUrl: string;
    fileName: string;
    title: string;
    description: string;
    presenterId: string;
    presenter: string;
    professionalImageUrl: string;
    durationMillis: number;
    viewCount: number;
    status: string;
    tags: string[];
    reactions: VideoReaction[];
    comments: VideoComment[];
    captions: VideoCaption[];
    extraMaterials: VideoExtraMaterial[];
    documentType: string;
    created: Date;
    updated: Date;
    viewingActivities: ViewingActivity[];
    visibleNewIcon: boolean;
    slideFlg: boolean;
    videoSlides: VideoSlide[];
    groupList: Group[];
    sttLanguage: string;
    languages: string[];
    captionLanguageList: CaptionLanguage[];
}

// 視聴履歴
export interface ViewingActivity {
    id: number;
    videoId: string;
    userId: string;
    created: string;
    progressMillis: number;
    isFinished: boolean;
}

export interface VideoChangeSet {
    title: string;
    description: string;
    presenter: string;
    tags: string[];
}

// ビデオアップロード
export interface AddVideoInfo {
    file: any;
    imageFile?: any;
    documentFile: File[];
    documentName: string[];
    video: VideoInfo;
}
export interface VideoExtraMaterial {
    id: number;
    videoId: number;
    fileName: string;
    contentType: string;
    fileSizeBytes: number;
    created: Date;
}

export interface VideoInfo {
    title: string;
    description: string;
    presenter: string;
    filename: string;
    tags?: number[];
    group: number[];
    bitrate: number;
    width: number;
    height: number;
    framerate: number;
    language: string;
    sttLanguage: string;
}

export interface EditVideoInfo {
    userId: string;
    file?: any;
    imageFile?: any;
    materialId: number[];
    documentFile: File[];
    documentName: string[];
    video: EditInfo;
}

export interface UploadDocumentIF {
    userId: string;
    videoId: number;
    materialId: number[];
    documentFile: File[];
    documentName: string[];
}

export interface EditInfo {
    id: number;
    title: string;
    description: string;
    imageUrl: string;
    presenter: string;
    tags?: number[];
    group: number[];
    languages: string;
}

export interface VideoPDF {
    slideUrl: string;
    slideName: string;
    isPublic: boolean;
    pdfUrl: string;
    videoSlides: VideoSlide[];
}
export interface VideoSlide {
    slideId: number;
    startMillis: number;
    videoId: number;
}

export interface UpdateVideoSlideIF {
    videoId: number;
    file: File | null;
    slideName: string;
    isPublic: boolean;
    videoSlides: VideoSlide[];
}

export function parseVideo(json: JsonAny): Video {
    return {
        ...json,
        extraMaterials: json.extraMaterials.map(parseVideoExtraMaterial),
        captions: json.captions.map(parseVideoCaption),
        comments: json.comments.map(parseVideoComment),
        reactions: json.reactions.map(parseVideoReaction),
        created: parseISO(json.created),
        updated: parseISO(json.updated),
    };
}
